import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { ComboBox } from '@/components/ComboBox';
import { useToast } from '@/components/Toast';
import {
  useCreateClient, useLookup, useClientGroupings, useClientPicker, type ClientPickerRow,
} from '@/api/hooks';
import { api, ApiError } from '@/api/client';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const US_CA = ['United States', 'Canada'];
// State/province lists per country (verbatim from Power Apps ComboBox_State).
const US_STATES = ['AK','AL','AR','AZ','CA','CO','CT','DC','DE','FL','GA','HI','IA','ID','IL','IN','KS','KY','LA','MA','MD','ME','MI','MN','MO','MS','MT','NC','ND','NE','NH','NJ','NM','NV','NY','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VA','VT','WA','WI','WV','WY'];
const CA_PROVINCES = ['AB','BC','MB','NB','NL','NS','NT','NU','ON','PE','QC','SK','YT'];

const EMAIL_RE = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const EIN1_RE = /^\d{3}-\d{2}-\d{4}$|^\d{2}-\d{7}$/;
const EIN2_RE = /^\d{3}-\d{2}-\d{4}$/;

type Form = Record<string, string> & { surveyOptIn?: boolean };

export function NewClient() {
  const navigate = useNavigate();
  const notify = useToast();
  const create = useCreateClient();
  const [acknowledged, setAcknowledged] = useState(false);
  const [confirmKind, setConfirmKind] = useState<'submit' | 'duplicate' | null>(null);

  // Identity + billing cascade (controlled, mirrors Power Apps ClientOnboarding).
  const [clientType, setClientType] = useState('');            // '' | 'Master' | 'New Sub'
  const [clientGrouping, setClientGrouping] = useState('');
  const [selectedClient, setSelectedClient] = useState<ClientPickerRow | null>(null);
  const [billOption, setBillOption] = useState('');            // '' | 'Self' | 'Master' | 'Other'
  const [billingGrouping, setBillingGrouping] = useState('');
  const [selectedBilling, setSelectedBilling] = useState<ClientPickerRow | null>(null);
  const [survey, setSurvey] = useState(false);                 // Checkbox_Survey → varSurvey

  const { register, handleSubmit, watch, getValues, setValue } = useForm<Form>({
    defaultValues: { clientSubId: '0' },
  });

  const managers = useLookup('client-managers');
  const industries = useLookup('brc-industries');
  const naics = useLookup('naics');
  const entityTypes = useLookup('entity-types');
  const countries = useLookup('countries');

  const groupings = useClientGroupings();
  const masterPicker = useClientPicker(clientType === 'New Sub' ? clientGrouping : undefined, false);
  const billingPicker = useClientPicker(billOption === 'Other' ? billingGrouping : undefined, true);

  const billOptions = clientType === 'Master' ? ['Self', 'Other'] : ['Self', 'Master', 'Other'];

  const w = watch();
  const country = w.country ?? '';
  const needsState = US_CA.includes(country);
  const stateItems = country === 'United States' ? US_STATES : country === 'Canada' ? CA_PROVINCES : [];

  // View-only Engagement Location, derived from the selected Client Manager
  // (Power Apps: LookUp(vw_EmployeeEngLocation, EmployeeName = ClientManager)).
  const engLocation = useQuery({
    queryKey: ['eng-location', w.clientManager ?? ''],
    queryFn: () => api.get<{ EngLocation: string }[]>(`/lookups/engagement-locations?partner=${encodeURIComponent(w.clientManager ?? '')}`),
    enabled: !!w.clientManager,
  });

  // Format validity (empty is allowed except where required) — Power Apps "!" checks.
  const clientEmailBad = !!w.clientEmail && !EMAIL_RE.test(w.clientEmail);
  const secEmailBad = !!w.secondaryEmail && !EMAIL_RE.test(w.secondaryEmail);
  const surveyEmailBad = !!w.surveyEmail && !EMAIL_RE.test(w.surveyEmail);
  const ein1Bad = !!w.taxSSN1 && !EIN1_RE.test(w.taxSSN1);
  const ein2Bad = !!w.taxSSN2 && !EIN2_RE.test(w.taxSSN2);
  const billStmtEmailBad = !!w.billingStatementEmail && !EMAIL_RE.test(w.billingStatementEmail);

  function changeType(t: string) {
    setClientType(t);
    setClientGrouping(''); setSelectedClient(null);
    setBillOption(''); setBillingGrouping(''); setSelectedBilling(null);
  }

  function changeCountry(v: string) {
    setValue('country', v);
    setValue('state', ''); // Country OnChange → Reset(ComboBox_State)
  }

  // Submit gate — mirrors Power Apps Button_Submit DisplayMode (incl. format checks).
  const invalid =
    !clientType ||
    (clientType === 'New Sub' && !selectedClient) ||
    !billOption ||
    (billOption === 'Other' && !selectedBilling) ||
    !w.clientName?.trim() || !w.contactName?.trim() || !w.mailingAddress1?.trim() ||
    !w.city?.trim() || !country || (needsState && !w.state?.trim()) ||
    !w.zipCode?.trim() || !w.phone?.trim() || !w.clientEmail?.trim() ||
    !w.clientManager || !w.entityType || !w.brcIndustry || !w.naics || !w.fiscalYearEndMonth ||
    !w.billingStatementEmail?.trim() ||
    clientEmailBad || secEmailBad || surveyEmailBad || ein1Bad || ein2Bad || billStmtEmailBad;

  async function submit(duplicate: boolean) {
    const v = getValues();
    const payload = {
      ...v,
      clientType,
      clientId: clientType === 'New Sub' ? (selectedClient?.ClientID ?? '') : '',
      clientSubId: '0',
      billingClientType: billOption,
      billingClientId: billOption === 'Other' ? (selectedBilling?.ClientID ?? '') : '',
    };
    try {
      await create.mutateAsync(payload);
      notify('success', duplicate
        ? 'Client saved successfully. You can now enter another client.'
        : 'Client request submitted.');
      if (duplicate) setConfirmKind(null);
      else navigate('/clients');
    } catch (e) {
      const msg = e instanceof ApiError ? (e.problem.title || 'Validation failed') : 'Submit failed';
      notify('error', typeof e === 'object' && e && 'problem' in e ? msg : 'Submit failed');
      setConfirmKind(null);
    }
  }

  if (!acknowledged) {
    return (
      <ConfirmDialog
        open
        message="I acknowledge that client acceptance procedures, including the conflict check, have been completed and stored in the appropriate location."
        confirmLabel="Yes"
        cancelLabel="No"
        onConfirm={() => setAcknowledged(true)}
        onCancel={() => navigate('/')}
      />
    );
  }

  const groupingOptions = (
    <>
      <option value="">— select —</option>
      {(groupings.data ?? []).map((g) => (
        <option key={g.ClientGrouping} value={g.ClientGrouping}>{g.ClientGrouping}</option>
      ))}
    </>
  );

  return (
    <form
      className="w-full space-y-4 p-5"
      onSubmit={handleSubmit(() => setConfirmKind('submit'))}
    >
      <div className="flex justify-end">
        <p className="text-right text-sm font-semibold text-[#3256a0]">
          Note: Client Acceptance must be complete prior to submitting this form.
        </p>
      </div>

      <div className="grid gap-4 xl:grid-cols-2 xl:items-start">
      <Section title="Client Information">
        <Field label="Type of Client *">
          <select className="input" value={clientType} onChange={(e) => changeType(e.target.value)}>
            <option value="">— select —</option>
            <option value="Master">New Master Client</option>
            <option value="New Sub">New Sub of Existing Client</option>
          </select>
        </Field>

        <Field label={`Client starts with ${clientType === 'New Sub' ? '*' : ''}`}>
          <select className="input" value={clientGrouping} disabled={clientType !== 'New Sub'}
            onChange={(e) => { setClientGrouping(e.target.value); setSelectedClient(null); }}>
            {groupingOptions}
          </select>
        </Field>

        <Field label={`Client ID ${clientType === 'New Sub' ? '*' : ''}`}>
          <ComboBox<ClientPickerRow>
            items={masterPicker.data ?? []}
            value={selectedClient}
            onChange={setSelectedClient}
            getLabel={(c) => c.DisplayText ?? `${c.ClientID} - ${c.ClientName}`}
            getKey={(c) => c.ClientID}
            placeholder="Search by ID or name…"
            disabled={clientType !== 'New Sub' || !clientGrouping}
            loading={masterPicker.isFetching}
          />
        </Field>

        <Field label="Client Name *"><input className="input" {...register('clientName')} /></Field>
        <Field label={<>Client Email * <Warn show={clientEmailBad} tip="Enter a valid email" /></>}>
          <input className="input" {...register('clientEmail')} />
        </Field>
        <Field label={<>Secondary Email <Warn show={secEmailBad} tip="Enter a valid email" /></>}>
          <input className="input" {...register('secondaryEmail')} />
        </Field>

        <Field label="Bill To *">
          <select className="input" value={billOption}
            onChange={(e) => { setBillOption(e.target.value); setBillingGrouping(''); setSelectedBilling(null); }}>
            <option value="">— select —</option>
            {billOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </Field>

        <Field label={`Billing Client starts with ${billOption === 'Other' ? '*' : ''}`}>
          <select className="input" value={billingGrouping} disabled={billOption !== 'Other'}
            onChange={(e) => { setBillingGrouping(e.target.value); setSelectedBilling(null); }}>
            {groupingOptions}
          </select>
        </Field>

        <Field label={`Billing Client ID ${billOption === 'Other' ? '*' : ''}`}>
          <ComboBox<ClientPickerRow>
            items={billingPicker.data ?? []}
            value={selectedBilling}
            onChange={setSelectedBilling}
            getLabel={(c) => c.ClientFullName ?? `${c.ClientID}-${c.ClientName}`}
            getKey={(c) => c.ClientFullName ?? `${c.ClientID}-${c.ClientName}`}
            placeholder="Search by ID or name…"
            disabled={billOption !== 'Other' || !billingGrouping}
            loading={billingPicker.isFetching}
          />
        </Field>

        <Field label={<>Billing Statement Email * <Warn show={billStmtEmailBad} tip="Enter a valid email" /></>}>
          <input className="input" {...register('billingStatementEmail')} />
        </Field>
      </Section>

      <Section title="Contact Details">
        <Field label="Contact Name *"><input className="input" {...register('contactName')} /></Field>
        <Field label="Mailing Address 1 *"><input className="input" {...register('mailingAddress1')} /></Field>
        <Field label="Mailing Address 2"><input className="input" {...register('mailingAddress2')} /></Field>
        <Field label="City *"><input className="input" {...register('city')} /></Field>
        <Field label="Country *">
          <LookupCombo data={countries.data} field="Value" value={country}
            onChange={changeCountry} placeholder="Select country…" />
        </Field>
        <Field label={`State/Province ${needsState ? '*' : ''}`}>
          <ComboBox<string>
            items={stateItems}
            value={w.state ? w.state : null}
            onChange={(s) => setValue('state', s ?? '')}
            getLabel={(s) => s}
            getKey={(s) => s}
            placeholder="Select state…"
            disabled={!needsState}
          />
        </Field>
        <Field label="Zip/Postal Code *"><input className="input" {...register('zipCode')} /></Field>
        <Field label="Phone *"><input className="input" {...register('phone')} /></Field>
        <Field label="Secondary Phone"><input className="input" {...register('secondaryPhone')} /></Field>
      </Section>

      <Section title="Other Details">
        <Field label="Client Manager *">
          <LookupCombo data={managers.data} field="ClientManager" value={w.clientManager ?? ''}
            onChange={(v) => setValue('clientManager', v)} placeholder="Select manager…" />
        </Field>
        <Field label="Engagement Location">
          <input className="input" value={String(engLocation.data?.[0]?.EngLocation ?? '')} disabled placeholder="Set by Client Manager" />
        </Field>
        <Field label="Entity Type *">
          <LookupCombo data={entityTypes.data} field="EntityType" value={w.entityType ?? ''}
            onChange={(v) => setValue('entityType', v)} placeholder="Select entity type…" />
        </Field>
        <Field label="BRC Industry *">
          <LookupCombo data={industries.data} field="Industry" value={w.brcIndustry ?? ''}
            onChange={(v) => setValue('brcIndustry', v)} placeholder="Select industry…" />
        </Field>
        <Field label="NAICS *">
          <LookupCombo data={naics.data} field="NAICS" value={w.naics ?? ''}
            onChange={(v) => setValue('naics', v)} placeholder="Search NAICS…" />
        </Field>
        <Field label="Fiscal Year End Month *">
          <select className="input" {...register('fiscalYearEndMonth')}>
            <option value="">— select —</option>
            {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        </Field>
        <Field label={<>Tax EIN / SSN1 <Warn show={ein1Bad} tip="Format: XX-XXXXXXX or XXX-XX-XXXX" /></>}>
          <input className="input" maxLength={11} placeholder="00-0000000" {...register('taxSSN1')} />
        </Field>
        <Field label={<>Tax SSN2 <Warn show={ein2Bad} tip="Format: XXX-XX-XXXX" /></>}>
          <input className="input" maxLength={11} placeholder="000-00-0000" {...register('taxSSN2')} />
        </Field>
        <Field label="Referral Source"><input className="input" {...register('referralSource')} /></Field>
      </Section>

      <Section title="Survey">
        <div className="flex items-center gap-2 col-span-full">
          <input
            id="surveyChk"
            type="checkbox"
            checked={survey}
            onChange={(e) => {
              setSurvey(e.target.checked);
              if (!e.target.checked) { setValue('surveyEmail', ''); setValue('surveyAttention', ''); }
            }}
          />
          <label htmlFor="surveyChk" className="text-sm font-semibold text-foreground">Survey</label>
          <span className="text-xs text-muted-foreground" title="Check if different than contact above.">ⓘ</span>
        </div>
        <Field label={<>Survey Email <Warn show={surveyEmailBad} tip="Enter a valid email" /></>}>
          <input className="input" disabled={!survey} {...register('surveyEmail')} />
        </Field>
        <Field label="Survey Attention">
          <input className="input" disabled={!survey} {...register('surveyAttention')} />
        </Field>
      </Section>
      </div>

      <div>
        <label className="label">Notes</label>
        <textarea className="input" rows={3} {...register('note')} />
      </div>

      <p className="text-xs font-semibold text-accent">*  Mandatory Fields</p>

      <div className="flex gap-3">
        <button type="submit" className="btn-primary" disabled={invalid || create.isPending}>Submit</button>
        <button type="button" className="btn-secondary" disabled={invalid || create.isPending} onClick={() => setConfirmKind('duplicate')}>
          Submit &amp; Duplicate
        </button>
      </div>

      <ConfirmDialog
        open={confirmKind !== null}
        message="Do you wish to submit the client form? Press 'Yes' to continue, 'No' to cancel."
        onConfirm={() => submit(confirmKind === 'duplicate')}
        onCancel={() => setConfirmKind(null)}
      />
    </form>
  );
}

/** ComboBox bound to a lookup table by a single display column (stores the string). */
function LookupCombo({ data, field, value, onChange, placeholder, disabled }: {
  data?: Record<string, unknown>[];
  field: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  const rows = Array.isArray(data) ? data : [];
  const selected = rows.find((r) => String(r[field] ?? '') === value) ?? null;
  return (
    <ComboBox
      items={rows}
      value={selected}
      onChange={(r) => onChange(r ? String(r[field] ?? '') : '')}
      getLabel={(r) => String(r[field] ?? '')}
      getKey={(r) => String(r[field] ?? '')}
      placeholder={placeholder}
      disabled={disabled}
    />
  );
}

function Warn({ show, tip }: { show: boolean; tip: string }) {
  if (!show) return null;
  return <span title={tip} className="ml-1 font-bold text-orange-500">!</span>;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <h3 className="mb-2 border-b border-border pb-2 text-sm font-semibold uppercase tracking-wide text-primary">{title}</h3>
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}
function Field({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}
